import { Injectable } from '@angular/core';
import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { EmptyIdName, IdName } from '@shared/models/id-name';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { buildImplantBasedTreatmentPreviewLine } from '@modules/rx-intuitive-teeth-diagram/utils/build-implant-based-treatment-preview-line';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-diagram/services/rx-intuitive-indication-rules.service';
import { extractAdditionalInfoIds } from '@modules/rx-intuitive-crown-additional-info/utils/extract-additional-info-ids';
import {
	getToothNumberById,
	IntuitiveToothForTreatment,
	TeethNumberingSystem,
	UnitTypesInBridge
} from '@modules/rx-intuitive-teeth-diagram/models';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { ShellQuery } from '@shared/store/shell/shell-query';

type ToIntuitiveToothForTreatmentDeps = {
	getUnitTypeIndicationForDiagram: (unitTypeId: Tooth['UnitTypeID']) => number;
	resolveSpecificationIdName: (specificationId: Tooth['SpecificationId']) => IdName<number | string>;
	resolveMaterialIdName: (materialId: Tooth['MaterialID']) => IdName<number | string> | null;
	resolveShadeSystemIdName: (shadeSystemId: Tooth['ShadeSystemId']) => IdName<number> | null;
	resolvePonticDesignIdName?: (ponticDesignId: PonticDesignEnum | null | undefined) => IdName<PonticDesignEnum> | null;
};

const toIntuitiveToothForTreatmentVm = (
	tooth: Tooth,
	teethNumberingSystem: TeethNumberingSystem,
	toothLastTouchedAt: Record<number, number>,
	includeCrownAdditionalInfo: boolean,
	deps: ToIntuitiveToothForTreatmentDeps
): IntuitiveToothForTreatment => {
	const toothNumberInSystem = getToothNumberById({
		toothId: tooth.ToothID ?? 0,
		teethNumberingSystem
	});

	const base: IntuitiveToothForTreatment = {
		toothId: tooth.ToothID ?? 0,
		bridgeId: tooth.BridgeIndex,
		toothNumberInSystem: toothNumberInSystem ?? 0,
		indicationId: deps.getUnitTypeIndicationForDiagram(tooth.UnitTypeID),
		lastTouchedAt: toothLastTouchedAt[tooth.ToothID ?? -1] ?? 0,
		specification: deps.resolveSpecificationIdName(tooth.SpecificationId),
		material: deps.resolveMaterialIdName(tooth.MaterialID),
		shadeSystem: deps.resolveShadeSystemIdName(tooth.ShadeSystemId),
		shadeBody: tooth.ShadeBody ?? null,
		customLabMaterialGroup: tooth.CustomLabMaterialGroup
	};

	if (tooth.ToothInBridgeTypeID === UnitTypesInBridge.Pontic && deps.resolvePonticDesignIdName) {
		base.ponticDesign = deps.resolvePonticDesignIdName(tooth.PonticDesignID);
	}

	return includeCrownAdditionalInfo ? { ...base, ...extractAdditionalInfoIds(tooth) } : base;
};

@Injectable()
export class RxIntuitiveToothTreatmentMapper {
	constructor(
		private readonly indicationsService: RxIntuitiveIndicationRulesService,
		private readonly rxMaterialRulesService: RxMaterialRulesService,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly shellQuery: ShellQuery
	) {}

	toIntuitiveToothForTreatment(
		tooth: Tooth,
		teethNumberingSystem: TeethNumberingSystem,
		toothLastTouchedAt: Record<number, number>,
		includeCrownAdditionalInfo: boolean
	): IntuitiveToothForTreatment {
		const getUnitTypeIndicationForDiagram: ToIntuitiveToothForTreatmentDeps['getUnitTypeIndicationForDiagram'] =
			!!tooth.ToothInBridgeTypeID
				? () => this.indicationsService.getBridgeIndicationForDiagram(tooth)
				: unitTypeId => this.indicationsService.getUnitTypeIndicationForDiagram(unitTypeId);

		const base = toIntuitiveToothForTreatmentVm(tooth, teethNumberingSystem, toothLastTouchedAt, includeCrownAdditionalInfo, {
			getUnitTypeIndicationForDiagram,
			resolveSpecificationIdName: specificationId => this.resolveSpecificationIdName(specificationId),
			resolveMaterialIdName: materialId => this.resolveMaterialIdName(materialId),
			resolveShadeSystemIdName: shadeSystemId => this.resolveShadeSystemIdName(shadeSystemId),
			resolvePonticDesignIdName: ponticDesignId => this.resolvePonticDesignIdName(ponticDesignId)
		});

		const implantsTreatmentSummary = buildImplantBasedTreatmentPreviewLine(tooth.ImplantsViewData);

		return { ...base, implantsTreatmentSummary };
	}

	private resolveSpecificationIdName(specificationId: Tooth['SpecificationId']): IdName<number | string> {
		if (specificationId == null || specificationId === SpecificationEnum.None) {
			return { ...EmptyIdName, Id: SpecificationEnum.None };
		}

		const name = this.rxMaterialRulesService.getSpecificationName(specificationId);

		return { Id: specificationId, Name: name || String(specificationId) };
	}

	private resolveMaterialIdName(materialId: Tooth['MaterialID']): IdName<number | string> | null {
		if (materialId == null) {
			return null;
		}

		const name = this.rxMaterialRulesService.getMaterialName(materialId);

		return { Id: materialId, Name: name || String(materialId) };
	}

	private resolveShadeSystemIdName(shadeSystemId: Tooth['ShadeSystemId']): IdName<number> | null {
		if (shadeSystemId == null) {
			return null;
		}

		const shadeSystem = this.rxShadeSystemRulesService.getShadeSystemSelection(shadeSystemId);

		if (!shadeSystem) {
			return null;
		}

		return { Id: shadeSystem.Id, Name: shadeSystem.Name };
	}

	private resolvePonticDesignIdName(id: PonticDesignEnum | null | undefined): IdName<PonticDesignEnum> | null {
		if (id == null) {
			return null;
		}

		const found = this.shellQuery.ponticDesigns?.find(d => d.Id === id);

		return found ? (found as IdName<PonticDesignEnum>) : { Id: id, Name: String(id) };
	}
}
